import { useTranslation } from 'react-i18next'

import { DECIMAL_PLACES_DEFAULT } from '@/constants'
import {
  formatCurrency,
  formatDate,
  formatNumber,
  useParamsState,
} from '@/helpers'
import { InventoryTransactionProps } from '@/types'
import {
  Button,
  EditIcon,
  TableColumns,
  TableColumnsColumnProps,
  Text,
} from '@mercai/clever'

import { TableInventoryTransactionProps } from './table-inventory-transaction.type'

import { EmptyState } from '@/components'
import { ROUTES_ADMIN } from '@/router'
import { Link } from 'react-router-dom'
import { SupplyInventoryTransactionDrawer } from './components'
import styles from './table-inventory-transaction.module.css'

export const TableInventoryTransaction = ({
  inventoryTransactions = [],
  isLoading = false,
}: TableInventoryTransactionProps) => {
  const { t } = useTranslation('screenInventoryDetail')

  const [params, setParams] = useParamsState({
    inventoryTransactionUuid: '',
  })

  const AssociatedColumn = ({
    record,
  }: {
    record: InventoryTransactionProps
  }) => {
    let title = ''
    let reference: string | JSX.Element = ''

    if (record.supplyOrderItem?.supplyOrder) {
      title = t('inventoryTransactionAssociateEmptyColumn')
      reference = record.supplyOrder?.reference ? (
        <Link
          to={`${ROUTES_ADMIN.SUPPLY.ORDER_MANAGEMENT.PURCHASE_ORDER.fullPath()}?search=${record.supplyOrder?.reference}&showUuid=${record.supplyOrder?.uuid}`}
          target="_blank"
          className={styles['associate-column-link']}
        >
          {`${record.supplyOrder?.reference} | ${record.supplyOrder?.supplyQuotation?.purchaseOrderCode}`}
        </Link>
      ) : (
        '-'
      )
    } else if (record.saleOrderItem) {
      title = record.saleOrderItem?.saleOrder?.buyer?.companyName || ''
      reference = record.saleOrderItem?.saleOrder?.reference || ''

      reference = record.saleOrderItem?.saleOrder?.reference ? (
        <Link
          to={`${ROUTES_ADMIN.ORDER_CENTER.ALL.SHOW.fullPath(record.saleOrderItem?.saleOrder?.uuid)}`}
          target="_blank"
          className={styles['associate-column-link']}
        >
          {record.saleOrderItem?.saleOrder?.reference}
        </Link>
      ) : (
        '-'
      )
    }

    return (
      <div>
        {title ? (
          <div>
            <Text>{title}</Text>
          </div>
        ) : null}
        {reference ? (
          <div>
            <Text
              variant="text-small"
              className={styles['associate-column-reference']}
            >
              {reference}
            </Text>
          </div>
        ) : null}
      </div>
    )
  }

  const columns: TableColumnsColumnProps<InventoryTransactionProps>[] = [
    {
      title: t('inventoryTransactionAssociateColumn'),
      key: 'associated-column',
      render: ({ record }) => <AssociatedColumn record={record} />,
    },
    {
      title: t('inventoryTransactionWarehouseColumn'),
      key: 'outflow-column',
      width: '12rem',
      render: ({ record }) => (
        <Text>{record?.supplierWarehouse?.name || '-'}</Text>
      ),
    },
    {
      title: t('inventoryTransactionCreatedAtColumn'),
      key: 'created-at-column',
      width: '12rem',
      render: ({ record }) => (
        <Text>{record?.createdAt ? formatDate(record?.createdAt) : '-'}</Text>
      ),
    },
    {
      title: t('inventoryTransactionInflowColumn'),
      key: 'inflow-column',
      width: '10rem',
      render: ({ record }) => (
        <Text>
          {record?.quantity > 0
            ? formatNumber(record?.quantity, undefined, 0)
            : 0}{' '}
          KG
        </Text>
      ),
    },
    {
      title: t('inventoryTransactionOutflowColumn'),
      key: 'outflow-column',
      width: '10rem',
      render: ({ record }) => (
        <Text>
          {record?.quantity < 0
            ? formatNumber(record?.quantity, undefined, 0)
            : 0}{' '}
          KG
        </Text>
      ),
    },
    {
      title: t('inventoryTransactionStockColumn'),
      key: 'stock-column',
      width: '10rem',
      render: ({ record }) => (
        <Text>
          {formatNumber(record?.inventoryQuantity || 0, undefined, 0)} KG
        </Text>
      ),
    },
    {
      title: t('inventoryTransactionCostColumn'),
      key: 'cost-column',
      width: '10rem',
      render: ({ record }) => (
        <Text>
          {formatCurrency(
            record?.cost || 0,
            'es-MX',
            record?.currency || 'USD',
            DECIMAL_PLACES_DEFAULT,
          )}
        </Text>
      ),
    },
    {
      title: t('inventoryTransactionTotalCostColumn'),
      key: 'total-cost-column',
      width: '16rem',
      render: ({ record }) => (
        <Text>
          {formatCurrency(
            Math.abs((record?.cost || 0) * (record?.quantity || 1)),
            'es-MX',
            record?.currency || 'USD',
            DECIMAL_PLACES_DEFAULT,
          )}
        </Text>
      ),
    },
    {
      title: '',
      key: 'actions',
      width: '4.5rem',
      render: ({ record }) => {
        const isEditable = !!record?.supplyOrderItemUuid
        return (
          <Button
            variant="secondary"
            model="square"
            disabled={!isEditable}
            isActive={isEditable}
            onClick={() =>
              isEditable
                ? setParams({
                  inventoryTransactionUuid: record?.uuid || '',
                })
                : null
            }
          >
            <EditIcon />
          </Button>
        )
      },
    },
  ]

  return (
    <div>
      <TableColumns
        isLoading={isLoading}
        columns={columns}
        data={inventoryTransactions}
      />

      {!isLoading && inventoryTransactions.length === 0 && (
        <EmptyState
          title={t('inventoryTransactionEmptyTitleTable')}
          description={''}
        />
      )}

      <SupplyInventoryTransactionDrawer
        isOpen={!!params?.inventoryTransactionUuid}
        onClose={() => setParams({ inventoryTransactionUuid: '' })}
        uuid={params?.inventoryTransactionUuid}
      />
    </div>
  )
}
